import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "@unified-checkout/design-system";
import type { IPreDefinedRange } from "libs/design-system/src/types/date_range_picker";
import type { IAppliedRanges, TPickerType } from "@app/dashboard/types/innerLayout/home/overview/date-ranges-compare";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useIntegrationsStore } from "@app/dashboard/store/integrations";
import {
  getComparedDates,
  customLocales,
  getDisabledDates,
  mainPreDefinedRanges,
  comparePreDefinedRanges,
  getPickerBtnText

} from "@app/dashboard/utils/dateRangePicker";
import { getLang } from "@app/dashboard/utils/localStorageUtils";
import styles from "./date-ranges-compare.module.scss";

const DateRangesCompare = ({ disabled = false }: { disabled?: boolean }) => {
  const { t } = useTranslation();
  const lang = getLang();

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const { filters, setFilters } = useFiltersStore((state) => state);
  const { isLoading: isIntegrationsLoading } = useIntegrationsStore();

  const mainDefaultPreDefinedRange: IPreDefinedRange | undefined = mainPreDefinedRanges(t)?.find(range => range.default);
  const compareDefaultPreDefinedRange: IPreDefinedRange | undefined = comparePreDefinedRanges(t)?.find(range => range.default);

  const [appliedRanges, setAppliedRanges] = useState<IAppliedRanges>({
    main: {
      index: mainDefaultPreDefinedRange?.index,
      name: mainDefaultPreDefinedRange?.name,
      start: mainDefaultPreDefinedRange?.selectionRange[0],
      end: mainDefaultPreDefinedRange?.selectionRange[1]
    },
    compare: {
      index: compareDefaultPreDefinedRange?.index,
      name: compareDefaultPreDefinedRange?.name,
      start: compareDefaultPreDefinedRange?.selectionRange[0],
      end: compareDefaultPreDefinedRange?.selectionRange[1],
    }
  });

  const { start: mainStartDate, end: mainEndDate, index: mainRangeIndex, name: mainRangeName } = appliedRanges.main;
  const { start: compareStartDate, end: compareEndDate, index: compareRangeIndex, name: compareRangeName } = appliedRanges.compare;

  useEffect(() => {
    const mainRange = mainPreDefinedRanges(t)?.find(range => range.default);
    const compareRange = comparePreDefinedRanges(t)?.find(range => range.default);

    setFilters({
      startDate: mainRange?.selectionRange[0],
      endDate: mainRange?.selectionRange[1],
      compareStartDate: compareRange?.selectionRange[0],
      compareEndDate: compareRange?.selectionRange[1],
      selectedRangeName: mainRange?.name,
    });
  }, []);

  const getPreDefinedRange = (pickerType: TPickerType, preDefinedRange: IPreDefinedRange[], selectionRange: [Date, Date], rangeIndex: number) => {

    const [startDate, endDate] = selectionRange || [];

    const disabledDates = getDisabledDates(startDate, endDate);

    return preDefinedRange.map((range) => {
      if (range.name === 'custom') {
        return {
          ...range,
          selectionRange: selectionRange,
          selected: range.index === rangeIndex,
          ...(
            pickerType === 'compare' ?
              { disabledRange: disabledDates }
              :
              {}
          )
        };
      }
      return {
        ...range,
        selected: range.index === rangeIndex,
      };
    });
  };

  const onApplyHandler = (pickerType: TPickerType, range?: [Date, Date], rangeIndex?: number, rangeName?: string) => {

    const [startDate, endDate] = range as [Date, Date];

    if (pickerType === 'compare') {
      const selectedRangeName = mainPreDefinedRanges(t)?.find(range => range.index === rangeIndex)?.name;

      setAppliedRanges((prevAppliedRange) => ({
        ...prevAppliedRange,
        compare: {
          index: rangeIndex,
          name: rangeName,
          start: startDate,
          end: endDate
        }
      }));

      // Set global state with the applied dates
      setFilters({
        compareStartDate: startDate,
        compareEndDate: endDate,
        selectedRangeName: selectedRangeName
      });

    } else if (pickerType === 'main') {

      const { compareStartDate, compareEndDate } = getComparedDates(startDate, endDate);

      const relatedCompareRangeName = comparePreDefinedRanges(t)?.find(range => range.index === rangeIndex)?.name;
      const selectedRangeName = comparePreDefinedRanges(t)?.find(range => range.index === rangeIndex)?.name;

      // incase of applying range on main picker set the selected and applied ranges in the compare picker
      setAppliedRanges(() => ({
        main: {
          index: rangeIndex,
          name: rangeName,
          start: startDate,
          end: endDate
        },
        compare: {
          index: rangeIndex,
          name: relatedCompareRangeName,
          start: compareStartDate,
          end: compareEndDate
        }
      }));

      // Set global state with the applied dates
      setFilters({
        startDate: startDate,
        endDate: endDate,
        compareStartDate: compareStartDate,
        compareEndDate: compareEndDate,
        selectedRangeName: selectedRangeName
      });
    };

  };

  const onClearDateHandler = () => {
    const mainStartDate = mainDefaultPreDefinedRange?.selectionRange[0];
    const mainEndDate = mainDefaultPreDefinedRange?.selectionRange[1];
    const compareStartDate = compareDefaultPreDefinedRange?.selectionRange[0];
    const compareEndDate = compareDefaultPreDefinedRange?.selectionRange[1];

    setAppliedRanges({
      main: {
        index: mainDefaultPreDefinedRange?.index,
        name: mainDefaultPreDefinedRange?.name,
        start: mainStartDate,
        end: mainEndDate
      },
      compare: {
        index: compareDefaultPreDefinedRange?.index,
        name: compareDefaultPreDefinedRange?.name,
        start: compareStartDate,
        end: compareEndDate
      }
    });

    setFilters({
      startDate: mainStartDate,
      endDate: mainEndDate,
      compareStartDate: compareStartDate,
      compareEndDate: compareEndDate,
      selectedRangeName: compareDefaultPreDefinedRange?.name
    });
  };

  return (
    <div className={styles["range-picker"]}>
      <DateRangePicker
        width={isMobile ? '100%' : 'fit-content'}
        lang={lang}
        {...(
          mainStartDate && mainEndDate ?
            { text: getPickerBtnText(t, mainStartDate, mainEndDate, mainRangeName) }
            :
            {}
        )}
        resetable
        onApply={(range, rangeIndex, rangeName) => onApplyHandler('main', range, rangeIndex, rangeName)}
        onClear={onClearDateHandler}
        customLocale={customLocales}
        preDefinedRanges={
          getPreDefinedRange(
            'main',
            mainPreDefinedRanges(t),
            [mainStartDate, mainEndDate] as [Date, Date],
            mainRangeIndex as number
          )
        }
        jumpToStartDateOnShortcutClick
        disabled={isIntegrationsLoading}
      />

      <div className={styles["range-picker__compare"]}>{t('innerLayout.homePage.overview.dateRangePicker.comparedTo')}</div>

      <DateRangePicker
        width={isMobile ? '100%' : 'fit-content'}
        lang={lang}
        {...(
          compareStartDate && compareEndDate ?
            { text: getPickerBtnText(t, compareStartDate, compareEndDate, compareRangeName) }
            :
            {}
        )}
        onApply={(range, rangeIndex, rangeName) => onApplyHandler('compare', range, rangeIndex, rangeName)}
        customLocale={customLocales}
        disabledApplyBtn={compareRangeName !== 'custom'}
        preDefinedRanges={
          getPreDefinedRange(
            'compare',
            comparePreDefinedRanges(t),
            [compareStartDate, compareEndDate] as [Date, Date],
            compareRangeIndex as number
          )
        }
        disabled={isIntegrationsLoading}
      />

    </div>
  )
}

export default DateRangesCompare;